export function formatCurrency(
  amount: number,
  options: {
    includeCents?: boolean;
    includeSymbol?: boolean;
    locale?: string;
  } = {}
): string {
  const {
    includeCents = true,
    includeSymbol = true,
    locale = 'en-ZA'
  } = options;
  
  const formatOptions: Intl.NumberFormatOptions = {
    style: includeSymbol ? 'currency' : 'decimal',
    currency: 'ZAR',
    minimumFractionDigits: includeCents ? 2 : 0,
    maximumFractionDigits: includeCents ? 2 : 0
  };
  
  try {
    return new Intl.NumberFormat(locale, formatOptions).format(amount);
  } catch {
    // Fallback formatting if locale is not supported
    const formattedNumber = includeCents 
      ? amount.toFixed(2)
      : Math.round(amount).toString();
    
    const withCommas = formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    return includeSymbol ? `R${withCommas}` : withCommas;
  }
}

export function formatCurrencyCompact(amount: number): string {
  if (amount >= 1000000) {
    return `R${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `R${(amount / 1000).toFixed(1)}K`;
  } else {
    return formatCurrency(amount, { includeCents: false });
  }
}

export function parseCurrencyInput(input: string): number | null {
  if (!input || typeof input !== 'string') {
    return null;
  }
  
  // Remove currency symbols, spaces, and commas
  const cleaned = input
    .replace(/[R\s,]/g, '')
    .trim();
  
  if (cleaned === '') {
    return null;
  }
  
  const parsed = parseFloat(cleaned);
  
  if (isNaN(parsed)) {
    return null;
  }
  
  return parsed;
}

export function formatPercentage(decimal: number, decimals = 1): string {
  return `${(decimal * 100).toFixed(decimals)}%`;
}

export function formatTaxBreakdown(
  amount: number,
  description: string,
  isDeduction = true
): string {
  const formattedAmount = formatCurrency(amount);
  const prefix = isDeduction ? '-' : '+';
  
  return `${description}: ${prefix}${formattedAmount}`;
}

export function formatIncomeComparison(
  grossIncome: number,
  netIncome: number
): string {
  const effectiveRate = grossIncome > 0 ? (grossIncome - netIncome) / grossIncome : 0;
  
  return `Gross: ${formatCurrency(grossIncome)} → Net: ${formatCurrency(netIncome)} (${formatPercentage(effectiveRate)} total deductions)`;
}